import React from 'react';
import { Loader2, Unplug } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DISCONNECT_COPY, type DisconnectIntegration } from './disconnectCopy';

interface DisconnectConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Runs when the user confirms. May be async — the dialog keeps its spinner
   * shown until it resolves, then the caller is responsible for closing it
   * (e.g. in the handler's success branch).
   */
  onConfirm: () => void | Promise<void>;
  /** Shows the spinner + disables the actions while a disconnect is in flight. */
  isLoading?: boolean;
  /**
   * Catalog key for the integration — pulls the canonical name + description
   * from `disconnectCopy.ts`. This is the preferred way to set copy; `name` /
   * `description` below only override it (e.g. a per-account "@handle" name).
   */
  integration?: DisconnectIntegration;
  /**
   * Name of the integration / account being disconnected, e.g. "Razorpay" or
   * "@my_handle". Overrides the catalog name; used to build the title +
   * default description.
   */
  name?: string;
  /** Override the default "Disconnect {name}?" title. */
  title?: string;
  /** Override the catalog/default description copy. */
  description?: React.ReactNode;
  /** Override the confirm button label (default "Disconnect"). */
  confirmLabel?: string;
}

/**
 * Shared confirmation dialog for disconnecting an account or integration.
 * Use this everywhere a disconnect action runs so the experience is identical
 * across Razorpay, PayU, Cashfree, WooCommerce, social accounts, calendars, etc.
 *
 * The confirm action calls `e.preventDefault()` internally so the dialog stays
 * open (with its spinner) while an async `onConfirm` runs — the caller closes
 * it via `onOpenChange(false)` once the disconnect succeeds.
 */
export const DisconnectConfirmDialog: React.FC<DisconnectConfirmDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
  integration,
  name,
  title,
  description,
  confirmLabel = 'Disconnect',
}) => {
  const catalog = integration ? DISCONNECT_COPY[integration] : undefined;
  const resolvedName = name ?? catalog?.name ?? 'this account';
  const resolvedTitle = title ?? `Disconnect ${resolvedName}?`;
  const resolvedDescription =
    description ??
    catalog?.description ??
    `This will disconnect ${resolvedName} from this business account. Related features will stop working until you reconnect. You can reconnect anytime.`;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{resolvedTitle}</AlertDialogTitle>
          <AlertDialogDescription>{resolvedDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // Keep the dialog open while an async disconnect runs; Radix would
              // otherwise close it immediately on click. Swallow rejections so a
              // failing disconnect (handlers may re-throw to keep the dialog
              // open for retry) doesn't surface as an unhandled promise.
              e.preventDefault();
              Promise.resolve(onConfirm()).catch(() => {});
            }}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Unplug className="h-4 w-4 mr-2" />
            )}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DisconnectConfirmDialog;
